import { z } from 'zod';
import { id, isoDateTime } from './common.js';
/** The seven-day row's words, one set for the recipient's card and the owner's Week page. */
export const WEEK_DAY_WORDS = {
    steady: 'steady',
    mixed: 'mixed',
    struggling: 'hard time',
};
export const WEEK_DAY_NO_READ = 'no read';
/** "Mon steady · Tue mixed · Wed hard time · …" */
export function weekDayRow(days) {
    return days
        .map((d) => {
        const name = new Date(`${d.day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
        return `${name} ${d.state ? WEEK_DAY_WORDS[d.state] : WEEK_DAY_NO_READ}`;
    })
        .join(' · ');
}
export const revokeShare = z.object({ shareId: id });
/* =========================================================================
 * THE SHARING LADDER — approved by Nick on 10 Sep 2026.
 *
 * docs/study-simpler-sharing-2026-09-10.md (section 4) is the design and
 * docs/plan-sharing-ladder-2026-09-10.md the build plan. Four nested levels and
 * two add-ons replaced ten ticks on 11 Sep 2026; build step 6 deleted the ticks,
 * their presets and the recap rules that read them.
 *
 * WHY GRANTS AND NOT THE OLD TICKS. Two old ticks cross a level boundary:
 * `mood` covered the day mood figures (every level now) and the per-check-in
 * table (What my days held and up); `thought_records` covered the worksheet
 * counts (held and up) and the worksheets themselves (My worksheets too and
 * up). Reusing those names with a changed meaning would make every
 * `scopes.includes('mood')` a silent misreading, so every gate reads a grant.
 *
 * ONE TABLE, READ BY EVERY SIDE. The route, the share screen, the worker, the
 * manual check and check-offer-locks all import LEVEL_CONTENTS. Copy that
 * describes a gate is a second implementation of it; this is the only one.
 * ========================================================================= */
export const shareLevel = z.enum(['seem', 'held', 'worksheets', 'everything']);
export const shareAddon = z.enum(['meds', 'events', 'homework']);
export const LEVEL_ORDER = shareLevel.options;
export const LEVEL_CONTENTS = {
    seem: {
        name: 'How I seem',
        weekly: 'seemed',
        grants: ['dayLine', 'dayMoodFigures', 'journalCount', 'weeklySeemed'],
        items: [
            {
                id: 'seem.line',
                name: 'How you seem, each day',
                what: 'Steady, mixed or a hard time, in two or three sentences, for today and the six days before. Written ' +
                    'from your figures, how heavy an entry was judged, a few topic words and the name of anything big going on ' +
                    'that day, which it may say where you named it. Never your entries.',
            },
            {
                id: 'seem.mood',
                name: 'Mood figures, each day',
                what: 'Check-ins, average, lowest and highest mood, focus, overwhelm, the time of the last check-in, and how ' +
                    'the day compares with your own fortnight.',
            },
            { id: 'seem.journalCount', name: 'How many journal entries', what: 'A count, never what they say.' },
            {
                id: 'seem.weekly',
                name: 'How the week seemed',
                weekly: true,
                what: 'A short reading written only from the daily lines this link showed, and whether something significant ' +
                    'was going on — and, where you named it, the name you gave it, in your words — beside the seven days, where each of them ended, and your ' +
                    'average mood. The latest four weeks; one before ' +
                    'the first full week at this level shows your average mood without the reading or the seven days.',
            },
        ],
    },
    held: {
        name: 'What my days held',
        weekly: 'held',
        grants: ['detailLine', 'checkinTable', 'helpNow', 'worksheetCounts', 'trendsTable', 'weeklyHeld'],
        items: [
            {
                id: 'held.line',
                name: 'What your days held',
                what: 'On days with something written, the line is written from everything the How I seem line is given, plus ' +
                    'your journal titles and summaries, your check-in notes and the thoughts on your worksheets, whether or ' +
                    'not the journal itself is shared. It is told never to quote you, an instruction rather than a check, and it can name people. Each day says which kind ' +
                    'of line it is. Only for days from when the link reached this level.',
            },
            { id: 'held.checkins', name: 'Your check-ins', what: 'Your latest 200, each with its time and all eight ratings, never what you typed with it.' },
            { id: 'held.helpNow', name: 'Help now moments', what: 'When, how bad it felt if rated, and the tool used.' },
            { id: 'held.worksheetCounts', name: 'Worksheet counts', what: 'Started and finished each day, never what they say.' },
            { id: 'held.table', name: 'The day-by-day table', what: 'Up to 120 days of figures, no free text.' },
            {
                id: 'held.weekly',
                name: 'What the week held',
                weekly: true,
                what: "A short account written only from that week's daily lines as this link showed them, and whether " +
                    "something significant was going on — and, where you named it, the name you gave it, in your words — beside the " +
                    "seven days and the week's figures, including " +
                    'how many days you logged anything, a dose included. The latest four weeks; one before the first full week ' +
                    'at this level shows its figures without the reading or the seven days.',
            },
        ],
    },
    worksheets: {
        name: 'My worksheets too',
        weekly: 'held',
        grants: ['worksheets'],
        items: [
            {
                id: 'worksheets.records',
                name: 'Your worksheets',
                what: 'Your latest 100, word for word, including worksheets the app drafted from a journal entry, which ' +
                    'carry what that entry said; the feelings and how strongly, and your belief ratings. Any thinking traps ' +
                    'the app picked are marked as its picks.',
            },
        ],
    },
    everything: {
        name: 'Everything',
        weekly: 'own_words',
        grants: ['journal', 'topicWords', 'weeklyOwnWords'],
        items: [
            {
                id: 'everything.journal',
                name: 'Your journal',
                what: 'The title and short summary of each of your latest 60 entries, which keep names and specifics. Never the entry itself or ' +
                    'the recording.',
            },
            {
                id: 'everything.topicWords',
                name: 'Topic words',
                what: 'Up to five on each day, picked by the app as it writes the How I seem line, from what that line is ' +
                    "given: your journal's subjects, but also your figures and the name of anything big going on.",
            },
            {
                id: 'everything.weekly',
                name: 'Your own weekly recap',
                weekly: true,
                what: 'In place of What the week held, the round-up written for you. It is written from your check-ins with ' +
                    'their notes and tags, your worksheets, your journal summaries (or up to 1,500 characters of an entry ' +
                    'with no summary) and the topic words on each journal entry, and your Help now moments. The latest four ' +
                    'weeks.',
            },
        ],
    },
};
export const ADDON_CONTENTS = {
    meds: {
        name: 'Medications',
        grants: ['meds'],
        items: [
            {
                id: 'meds',
                name: 'Medications',
                what: "What you take, including ones you have stopped, and doses for the latest 90 days that had one scheduled: taken, missed, skipped or assumed. " +
                    "Also dose figures on each day and week, and that day's doses on each worksheet when worksheets are shared.",
            },
        ],
    },
    homework: {
        name: 'Therapy homework',
        grants: ['homework'],
        items: [
            {
                id: 'homework',
                name: 'Therapy homework',
                what: 'The questions you were set and what you wrote for them, day by day, in your own words. ' +
                    'Homework can name people, including the person this link belongs to. ' +
                    'Never how many days you wrote on out of how many, and never a day you left empty.',
            },
        ],
    },
    events: {
        // "Threads" since 14 Oct 2026: one noun for the thing, on his screens and
        // on a recipient's. A thread is a life event, big or small — something
        // shaping the days. The grant and the id keep their old names.
        name: 'Threads',
        grants: ['lifeEvents'],
        items: [
            {
                id: 'events',
                name: 'Threads',
                what: 'The threads you have marked that are running today — a life event, big or small, something shaping your days — by the name you gave each or “Something big”, and how long each has run. It ' +
                    "also lets what the app writes on this link say that name — the daily lines and the weekly readings may use " +
                    'the words you gave it, where what is written calls for them — and lets a week with something going on show ' +
                    'its reading. A link without it is shown none of that: a line using the name is held back whole, and so is ' +
                    "that week's reading.",
            },
        ],
    },
};
/** What the owner is asked before a link gains something. The server enforces the ids. */
export const CONFIRMATIONS = {
    held: "From today, on days with something written, this link's daily line is written from everything the How I seem " +
        'line is given (your figures, how heavy an entry was judged, a few topic words and the name of anything big ' +
        'going on that day), plus your ' +
        'journal titles and summaries, your check-in notes and the thoughts on your worksheets, whether or not the ' +
        'journal itself is shared. It is told never to quote you, an instruction rather than a check, and it can name ' +
        'people, including the person you are sharing with. They also get your latest 200 check-ins with their eight ratings, your Help now moments, your worksheet counts and ' +
        'the day-by-day table, and each week becomes What the week held, written from those daily lines. Days before ' +
        'today keep the shorter line.',
    worksheets: 'Your latest 100 worksheets, word for word: the situation, the thought, the evidence and your answer, the feelings and how ' +
        'strongly, and your belief ratings — including worksheets the app drafted from a journal entry, which carry what ' +
        'that entry said. Any thinking traps the app picked are marked as its picks. A worksheet can name people, and ' +
        'the name of a thread you have marked, even on a link without Threads.',
    everything: 'Your journal: the title and summary of each of your latest 60 entries, which keep names and specifics, and the topic words on each ' +
        'day. And your own weekly recap for the latest four weeks, in place of What the week held. It is written from ' +
        'your check-ins with their notes and tags, your worksheets, your journal summaries (or up to 1,500 characters of ' +
        'an entry with no summary) and the topic words on each journal entry, and your Help now moments. Journal titles ' +
        "and summaries, worksheets and your recap are not checked for a thread's name; topic words are.",
    /*
     * The yes for homework, and it has to say the part that is easy to miss: the
     * person who SET the homework and the person it is ABOUT can be two different
     * people, and only one of them is reading this link. Homework about Eui-Jip,
     * shared with the therapist who asked for it, is the case this exists for —
     * and the same sentence warns what it would mean to put this add-on on
     * Eui-Jip's link instead.
     */
    homework: 'The questions you were set and everything you wrote for them, in your own words, day by day. ' +
        'What you write can name people — including whoever this link belongs to, and anyone the homework is about. ' +
        'It never says how many days you wrote on, and a day you left empty is simply absent. ' +
        'Turning this off again takes one tap and asks nothing.',
    meds: 'What you take, including medications you have stopped, and every dose for the latest 90 days that had one scheduled: taken, missed, skipped or ' +
        "assumed. With worksheets, each worksheet shows that day's doses and how you said they felt.",
    events: 'Anything big you have marked that is going on today, by the name you gave it or “Something big”, and how long it has run. ' +
        'From now on the daily lines and the weekly readings on this link may also SAY that name — your words, where what is ' +
        'written calls for them, and no fact you did not write there. Days and weeks keep their words afterwards, so one of ' +
        'them can name something that has since ended. A link without this add-on is shown none of it: a line using the ' +
        "name is held back whole, on any day it covered, and so is that week's reading.",
};
/** The label on each day card, by the kind of line it shows. The display name, never a guessed pronoun. */
export const DAY_LINE_LABEL = {
    held: (name) => `What ${name}'s day held`,
    seemed: (name) => `How ${name} seemed`,
};
/** Which grant each figure answers to: on the day block, the strip, the folds and the weekly cards. */
export const FIGURE_GRANT = {
    checkins: 'dayMoodFigures',
    meanMood: 'dayMoodFigures',
    /* Where the day ended. The same class of figure as the lowest and highest
       below, and less than either: one rating he gave, not the worst moment. */
    lastMood: 'dayMoodFigures',
    lowestMood: 'dayMoodFigures',
    highestMood: 'dayMoodFigures',
    lastCheckinAt: 'dayMoodFigures',
    meanFocus: 'dayMoodFigures',
    meanOverwhelm: 'dayMoodFigures',
    trend: 'dayMoodFigures',
    bestDay: 'dayMoodFigures',
    hardestDay: 'dayMoodFigures',
    sosEvents: 'helpNow',
    sosRelief: 'helpNow',
    thoughtRecords: 'worksheetCounts',
    thoughtRecordsCompleted: 'worksheetCounts',
    journalEntries: 'journalCount',
    voiceLogs: 'journalCount',
    medsTaken: 'meds',
    medsAssumed: 'meds',
    medsScheduled: 'meds',
    medAdherenceRate: 'meds',
    // How many days anything was logged, a dose included: held and up.
    activeDays: 'checkinTable',
};
/** Which grant each column of the day-by-day table answers to. The page hides exactly the columns nulled. */
export const TREND_COLUMN_GRANT = {
    checkins: 'dayMoodFigures',
    mean_mood: 'dayMoodFigures',
    /* Where the day ended. Same rung as the mean beside it, and the figure the
       plate is drawn from — a rating he gave, not an average of several. */
    last_mood: 'dayMoodFigures',
    mean_focus: 'dayMoodFigures',
    thought_records: 'worksheetCounts',
    voice_logs: 'journalCount',
    sos_events: 'helpNow',
    meds_taken: 'meds',
    meds_scheduled: 'meds',
};
/** The only figures a weekly card may carry, by kind. Fail-closed: unlisted keys never travel. */
export const WEEKLY_FIGURES = {
    seemed: ['meanMood', 'medAdherenceRate'],
    held: ['activeDays', 'checkins', 'meanMood', 'bestDay', 'hardestDay', 'thoughtRecords', 'voiceLogs', 'sosEvents', 'medAdherenceRate'],
    own_words: ['activeDays', 'checkins', 'meanMood', 'bestDay', 'hardestDay', 'thoughtRecords', 'voiceLogs', 'sosEvents', 'medAdherenceRate'],
};
/** How many weeks every weekly card list shows. */
export const WEEKLY_CARD_WEEKS = 4;
export function levelAtLeast(level, min) {
    return LEVEL_ORDER.indexOf(level) >= LEVEL_ORDER.indexOf(min);
}
/** Add-ons once each, in the canonical order (Medications before Threads); anything unknown dropped. */
export function normaliseAddons(addons) {
    return shareAddon.options.filter((a) => addons.includes(a));
}
/** Everything a level and its add-ons grant, by the table: each level includes the ones below. */
export function grantsFor(level, addons) {
    const out = new Set();
    for (const l of LEVEL_ORDER) {
        for (const g of LEVEL_CONTENTS[l].grants)
            out.add(g);
        if (l === level)
            break;
    }
    for (const a of normaliseAddons(addons))
        for (const g of ADDON_CONTENTS[a].grants)
            out.add(g);
    return out;
}
/**
 * What a link may show: what its level grants now, but never more than the link
 * was given. So a later change that adds something to a level does not reach an
 * existing link until the owner says yes (the manual promises exactly this).
 */
export function effectiveGrants(link) {
    const given = new Set(link.grants);
    return new Set([...grantsFor(link.level, link.addons)].filter((g) => given.has(g)));
}
/**
 * What a link's level grants now that the link was not given: empty unless a
 * later change added something to a level. effectiveGrants keeps these off the
 * link; the share screen says so, and re-saving asks for that level's yes.
 */
export function notYetGiven(link) {
    const given = new Set(link.grants);
    return [...grantsFor(link.level, link.addons)].filter((g) => !given.has(g));
}
/** The weekly a set of grants gets: the highest present, or none (figures only). */
export function weeklyKindFor(grants) {
    if (grants.has('weeklyOwnWords'))
        return 'own_words';
    if (grants.has('weeklyHeld'))
        return 'held';
    if (grants.has('weeklySeemed'))
        return 'seemed';
    return null;
}
/**
 * The confirmations needed to give a link `to`, against what it was given.
 *
 * One rule for making and for changing a link: each level from What my days held
 * up to `to.level`, and each add-on in `to`, whose own grants the link does not
 * already hold. A made link counts as holding nothing. How I seem is never asked
 * about. Moving down asks nothing, and a partial confirmation is refused.
 */
export function confirmationsNeeded(from, to) {
    const have = new Set(from?.grants ?? []);
    const needed = [];
    for (const l of LEVEL_ORDER.slice(1, LEVEL_ORDER.indexOf(to.level) + 1)) {
        if (LEVEL_CONTENTS[l].grants.some((g) => !have.has(g)))
            needed.push(l);
    }
    for (const a of normaliseAddons(to.addons)) {
        if (ADDON_CONTENTS[a].grants.some((g) => !have.has(g)))
            needed.push(a);
    }
    return needed;
}
/**
 * The dates after a link is made (`from` null) or moved.
 *
 *   detailSince  set when the link gains fuller lines (made at What my days held
 *                or above, or moved up out of How I seem); kept while it stays
 *                at those levels; cleared on a move down to How I seem.
 *   weeklySince  set when made, and again whenever its weekly kind changes.
 *
 * So on a link whose weekly is What the week held, detailSince ≤ weeklySince:
 * every week that link shows is made of days it showed fuller lines on, and one
 * stored held reading per week is true for every link that shows it.
 */
export function sinceAfterMove(from, to, today) {
    const toGrants = grantsFor(to.level, to.addons);
    if (!from)
        return { detailSince: toGrants.has('detailLine') ? today : null, weeklySince: today };
    const fromGrants = grantsFor(from.level, from.addons);
    const detailSince = !toGrants.has('detailLine')
        ? null
        : fromGrants.has('detailLine') && from.detailSince !== null
            ? from.detailSince
            : today;
    const weeklySince = weeklyKindFor(fromGrants) === weeklyKindFor(toGrants) ? from.weeklySince : today;
    return { detailSince, weeklySince };
}
/** Whether a link shows the fuller line on a day. The writer and the route call this same function. */
export function detailShownOn(link, day) {
    const g = link.grants ? effectiveGrants(link) : grantsFor(link.level, link.addons);
    return g.has('detailLine') && link.detailSince !== null && day >= link.detailSince;
}
/** The earliest day any of these links shows fuller lines on, or null: the floor for writing them. */
export function detailFloor(links) {
    let floor = null;
    for (const l of links) {
        if (l.detailSince === null)
            continue;
        if (!detailShownOn(l, l.detailSince))
            continue;
        if (floor === null || l.detailSince < floor)
            floor = l.detailSince;
    }
    return floor;
}
/** The first day a link's weekly counts from: a week is live only if it starts on or after it. */
export function weeklyLiveFrom(link) {
    const since = link.visibleFrom ? link.visibleFrom.slice(0, 10) : '';
    return since > link.weeklySince ? since : link.weeklySince;
}
/** What a link shows, as items: every level up to its own, only its own weekly, and its add-ons. */
export function itemsFor(level, addons) {
    const kind = LEVEL_CONTENTS[level].weekly;
    const weeklyOwner = LEVEL_ORDER.find((l) => LEVEL_CONTENTS[l].weekly === kind && LEVEL_CONTENTS[l].items.some((i) => i.weekly));
    const out = [];
    for (const l of LEVEL_ORDER) {
        out.push(...LEVEL_CONTENTS[l].items.filter((i) => !i.weekly));
        if (l === level)
            break;
    }
    const weekly = LEVEL_CONTENTS[weeklyOwner].items.find((i) => i.weekly);
    if (weekly)
        out.push(weekly);
    for (const a of normaliseAddons(addons))
        out.push(...ADDON_CONTENTS[a].items);
    return out;
}
/** What a link does not show: the levels above its own, and the add-ons it lacks. */
export function itemsWithheld(level, addons) {
    const chosen = normaliseAddons(addons);
    const out = [];
    for (const l of LEVEL_ORDER.slice(LEVEL_ORDER.indexOf(level) + 1)) {
        out.push(...LEVEL_CONTENTS[l].items.filter((i) => !i.weekly));
    }
    for (const a of shareAddon.options)
        if (!chosen.includes(a))
            out.push(...ADDON_CONTENTS[a].items);
    return out;
}
/** What changes when a link moves, as item names: the change card's record. */
export function contentsDiff(from, to) {
    const before = new Set(from ? itemsFor(from.level, from.addons).map((i) => i.name) : []);
    const after = new Set(itemsFor(to.level, to.addons).map((i) => i.name));
    return {
        added: [...after].filter((n) => !before.has(n)),
        removed: [...before].filter((n) => !after.has(n)),
    };
}
/** A copy with every mapped figure the grants lack set to null; unmapped keys are left as they are. */
export function stripFigures(obj, grants, figureGrant = FIGURE_GRANT) {
    const out = { ...obj };
    for (const key of Object.keys(out)) {
        const g = figureGrant[key];
        if (g && !grants.has(g))
            out[key] = null;
    }
    return out;
}
/**
 * The figures a weekly card may carry: only the keys listed for its kind, each
 * with a grant this link holds. A listed key with no grant at all is dropped —
 * a new figure fails closed until someone decides who may see it.
 */
export function pickWeeklyFigures(stats, kind, grants, figures = WEEKLY_FIGURES, figureGrant = FIGURE_GRANT) {
    const out = {};
    for (const key of figures[kind]) {
        const g = figureGrant[key];
        if (!g || !grants.has(g) || !(key in stats))
            continue;
        out[key] = stats[key];
    }
    return out;
}
/** The confirmations the server accepts, one for each ConfirmationId. */
export const confirmationId = z.enum(['held', 'worksheets', 'everything', 'meds', 'events', 'homework']);
const _everyConfirmation = confirmationId.options;
void _everyConfirmation;
/**
 * Making a link: who it is for, one level, its add-ons, and the confirmations
 * given for what it adds (confirmationsNeeded). The server refuses a link whose
 * confirmations are missing, so the screen's sheet is not the only gate.
 *
 * STRICT, so a body from before the ladder — one carrying `scopes` — is refused
 * rather than read as How I seem with its ticks silently dropped.
 */
export const createShare = z
    .object({
    label: z.string().min(1).max(120),
    granteeEmail: z.string().email().nullable().default(null),
    level: shareLevel,
    addons: z.array(shareAddon).max(3).default([]),
    confirmed: z.array(confirmationId).max(6).default([]),
    /** Days until the link stops working. Null means no expiry — discouraged. */
    expiresInDays: z.number().int().min(1).max(365).nullable().default(90),
    /** Hide entries before this date, e.g. only share since the last appointment. */
    visibleFrom: isoDateTime.nullable().default(null),
    /** Require the recipient to sign in rather than relying on link possession. */
    requireAccount: z.boolean().default(false),
    /** Circle (release 5 of 2.0): the role this link was made for, a starting point only. */
    role: z.enum(['therapist', 'partner', 'friend']).nullable().default(null),
    /** Other names the holder goes by, for the partner rule. The label is always one. */
    holderNames: z.array(z.string().min(1).max(60)).max(6).default([]),
})
    .strict()
    // Without an address to check against, "make them sign in" degrades to "any
    // account on this server will do", which is a weaker gate than the stricter-
    // sounding option has any business being. Requiring the email here means the
    // check at the recipient route always has something to compare.
    .refine((v) => !v.requireAccount || (v.granteeEmail?.trim().length ?? 0) > 0, {
    message: 'Add the address they will sign in with, or turn off the sign-in requirement.',
    path: ['granteeEmail'],
});
/** Changing what a link shows, in place: the same URL, the same confirmation rule as making one. */
export const changeShareLevel = z
    .object({
    level: shareLevel,
    addons: z.array(shareAddon).max(3).default([]),
    confirmed: z.array(confirmationId).max(6).default([]),
})
    .strict();
/**
 * Circle: the three roles. Release 5 of 2.0 (docs/PRD-2.0.md §5).
 *
 * A role is a STARTING POINT over the grants, not a grant of its own: choosing
 * one sends the same level and add-ons through `confirmationsNeeded` as any
 * change, and a link stores its grants as before. The September presets were
 * removed because a chip skipped the confirmations; these cannot, because the
 * only thing a role does is fill in the form.
 */
export const shareRole = z.enum(['therapist', 'partner', 'friend']);
export const ROLE_PRESETS = {
    therapist: {
        name: 'Therapist',
        level: 'worksheets',
        addons: ['meds', 'events', 'homework'],
        blurb: 'How each day went and the figures, worksheets word for word, the homework they set, medications, and anything big by name. Never journal entries, day marks or people.',
    },
    partner: {
        name: 'Partner',
        level: 'seem',
        addons: ['events'],
        // Copy that describes a gate: kept true to LEVEL_CONTENTS.seem, which grants
        // the day figures (the audit of 15 Oct 2026 found "Never figures" false).
        blurb: "Today's word, where each day ended and how the week seemed, anything big by name, and — if you write one — what helps. Never worksheets, entries, people, or anything about them.",
    },
    friend: {
        name: 'Friend',
        level: 'seem',
        addons: [],
        blurb: "Today's word, where each day ended and how the week seemed. Nothing named, nothing written by you.",
    },
};
/** The owner's per-link Circle settings. */
export const circleBody = z
    .object({
    whatHelps: z.string().max(600).nullable(),
    whatHelpsOn: z.boolean(),
    wavesOn: z.boolean(),
    holderNames: z.array(z.string().min(1).max(60)).max(6),
    role: shareRole.nullable(),
    /** The next appointment, for a therapist link's session brief. Null clears it. */
    sessionOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    /** A line from the owner for this person (14 Oct 2026). His words, shown while noteOn. */
    note: z.string().max(600).nullable(),
    noteOn: z.boolean(),
})
    .partial()
    .strict();
//# sourceMappingURL=sharing.js.map